import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { api, type ApiError } from '@/lib/api';
import { afterPromotionChange, qk } from '@/lib/query-keys';
import type { Promotion, PromotionPreflight, PromotionTarget } from '@/types/api';

/* ---------------------------------------------------------------------------
   Promotion, in two phases, because that is what the contract is.

   PHASE 1 — pre-flight. A GET that writes nothing: `git fetch --prune` then
   `git merge-tree --write-tree` per repo, plus, for production, the per-repo git
   ancestry check across every repo. It exists as its own request so the expert
   learns WHICH REPO CONFLICTS BEFORE ANYTHING MERGES. It is deliberately not
   cached beyond a few seconds and never polled: it is a point-in-time answer
   about refs that other people can move.

   PHASE 2 — the merge. A POST carrying `preflight_token`, which pins the exact
   SHAs that were pre-flighted. Phase 2 re-reads each target ref and compares, so
   a push that landed in between is a 422 rather than a silent merge of code
   nobody reviewed. That is the whole reason the token exists, and it is why a
   422 must never be retried with the same token.

   `can_promote: false` with populated `blocked_reasons` is a NORMAL answer, not
   an error. Every reason arrives at once, so the UI renders all of them rather
   than making an expert discover them one round trip at a time.
--------------------------------------------------------------------------- */

export function usePreflight(
  chatId: number,
  target: PromotionTarget,
  enabled: boolean,
): UseQueryResult<PromotionPreflight, ApiError> {
  return useQuery({
    queryKey: qk.preflight(chatId, target),
    queryFn: () => api.get<PromotionPreflight>(`/expert/chats/${chatId}/promotions/preflight`, { target }),
    enabled,
    // The refs it read can move under it. A stale pre-flight on screen is how an
    // expert presses a button carrying a token the server will refuse.
    staleTime: 0,
    gcTime: 30_000,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

export interface PromoteVariables {
  preflightToken: string;
  note?: string;
}

/**
 * Start the sequential merge.
 *
 * Returns 202: the promotion is a job, and `data.promotion.repos` is the durable
 * per-repo state machine it drives. Progress arrives on the stream; this
 * response is only the handle.
 *
 * A 409 refuses with NOTHING MERGED and carries the whole pre-flight in its
 * body, which is why `ApiError.preflight` exists — the refusal and the pre-flight
 * report are the same shape, so the dialog can render the refusal in place
 * rather than as a toast that loses the detail.
 */
export function usePromote(chatId: number, target: PromotionTarget) {
  const qc = useQueryClient();
  return useMutation<Promotion, ApiError, PromoteVariables>({
    mutationFn: ({ preflightToken, note }) =>
      api.post<Promotion>(`/expert/chats/${chatId}/promotions/${target}`, {
        preflight_token: preflightToken,
        ...(note ? { note } : {}),
      }),
    onSettled: () => afterPromotionChange(qc, chatId),
  });
}
